import { Routes, Route } from 't-lib-ui/router';
import Page from './pages/Page';
import { GlobalKeyHandler, ModalContainer } from 't-lib-ui/structure';
import Signup from './pages/auth/Signup';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import OneTimePassword from './pages/auth/OneTimePassword';
import ConfirmEmail from './pages/auth/ConfirmEmail';
import AcceptTerms from './pages/auth/AcceptTerms';
import Logout from './pages/auth/Logout';
import LoggedOut from './pages/auth/LoggedOut';
import Main from './pages/Main';
import HomePage from './pages/HomePage';
import HomeCategoryPage from './pages/HomeCategoryPage';
import AddFeedPage from './pages/AddFeedPage';
import AddNewsletterPage from './pages/AddNewsletterPage';
import MyFeedsPage from './pages/MyFeedsPage';
import AdminFeedsPage from './pages/admin/AdminFeedsPage';
import AdminViewFeedPage from './pages/admin/AdminViewFeedPage';
import AdminEditCatalogPage from './pages/admin/AdminEditCatalogPage';
import AdminCatalogsPage from './pages/admin/AdminCatalogsPage';
import AdminDiscoverPage from './pages/admin/AdminDiscoverPage';
import AdminImagesPage from './pages/admin/AdminImagesPage';
import AdminEditImagePage from './pages/admin/AdminEditImagePage';
import AdminViewUserPage from './pages/admin/AdminViewUserPage';
import AdminManageTagsPage from './pages/admin/AdminManageTagsPage';
import AdminInvitationsPage from './pages/admin/AdminInvitationsPage';
import AdminViewInvitationPage from './pages/admin/AdminViewInvitationPage';
import AdminRepairFeedPage from './pages/admin/AdminRepairFeedPage';
import AdminDebugFeedPage from './pages/admin/AdminDebugFeedPage';
import AdminFeedImportPage from './pages/admin/AdminFeedImportPage';
import AdminAutoFeedsPage from './pages/admin/AdminAutoFeedsPage';
import AdminProcessFeedPage from './pages/admin/AdminProcessFeedPage';
import AdminInactiveFeedsPage from './pages/admin/AdminInactiveFeedsPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminRetentionPage from './pages/admin/AdminRetentionPage';
import AccountPage from './pages/AccountPage';
import ImportOpmlPage from './pages/ImportOpmlPage';
import ImportAnonFeedsPage from './pages/auth/ImportAnonFeedsPage';
import ImportPage from './pages/ImportPage';
import PreferencesPage from './pages/PreferencesPage';
import FeedFilterPage from './pages/FeedFilterPage';
import HelpPage from './pages/HelpPage';
import ViewFeedByUrlPage from './pages/ViewFeedByUrlPage';
import OfflinePage from './pages/OfflinePage';
import { useContext } from 'react';
import { AppState } from './context/general';
import { useOfflineMode, useCanReadOffline } from './utils/useOfflineMode';
import { RouterContext, redirect, useLocation } from 't-lib-ui/router';

// Pages that can be accessed without being logged in
const unauthPages = ['/login', '/signup', '/forgot-password', '/reset-password', '/enter-password', '/confirm-email', '/accept-terms', '/logout', '/logged-out', '/add'];
// Pages that can be accessed without having a confirmed email
const unconfirmedPages = ['/confirm-email', ...unauthPages];

const AppRouter: React.FC = () => {
  const location = useLocation();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const loginState = appState.state.loginState;
  const offlineMode = useOfflineMode();
  const canReadOffline = useCanReadOffline();

  const simplifiedPage = location.pathName.replace(/^(\/[a-z-]+).*$/, '$1');

  // Anonymous is the default: unauthenticated (logged-out) visitors are NOT forwarded to /login —
  // they use the app routes directly in anon mode. The only remaining forward is for an account
  // that exists but hasn't confirmed its email yet, which must finish that flow first.
  if (loginState == 'unconfirmed' && !unconfirmedPages.includes(simplifiedPage))
    return redirect(routerCtx, '/confirm-email');

  // A session that should be valid but whose cookie was lost (typically iOS PWA cookie eviction)
  // lands in reloginRequired — send it to the login screen, while leaving the public/auth pages
  // (login, signup, /add, etc.) reachable so the user can re-auth or browse anonymously.
  if (loginState == 'reloginRequired' && !unauthPages.includes(simplifiedPage))
    return redirect(routerCtx, '/login');

  // Homepage "Log in" entry point: send an already-signed-in user straight into the app, and
  // everyone else to the login screen. Lets the marketing homepage link one stable URL without
  // knowing the auth state. (unconfirmed/reloginRequired are already redirected above, so by here
  // loginState is either 'ok' or 'loggedOut'.)
  if (simplifiedPage == '/login-start')
    return redirect(routerCtx, loginState == 'ok' ? '/a/home' : '/login');

  const routes: Route[] = [
    // The native apps start at their bundle's index.html.
    {path: '/index.html', redirect: '/'},
    {path: '/', redirect: '/a'},
    {path: '/a', redirect: '/a/home'},
    {path: '/a/new', component: <Main />},
    {path: '/a/new/{folder}', component: <Main />},
    {path: '/a/home', component: <HomePage />},
    {path: '/a/home/{category}', component: <HomeCategoryPage />},
    {path: '/a/myfeeds', component: <MyFeedsPage />},
    {path: '/a/add', component: <AddFeedPage />},
    {path: '/a/add/newsletter', component: <AddNewsletterPage />},
    {path: '/a/add/{category}', component: <AddFeedPage />},
    {path: '/a/add/{category}/{subCategory}', component: <AddFeedPage />},
    {path: '/a/account', component: <AccountPage />},
    {path: '/a/import-opml', component: <ImportOpmlPage />},
    {path: '/a/import-feeds', component: <ImportAnonFeedsPage />},
    {path: '/a/import', component: <ImportPage />},
    {path: '/a/preferences', component: <PreferencesPage />},
    {path: '/a/help', component: <HelpPage />},
    {path: '/a/help/{topic}', component: <HelpPage />},
    {path: '/a/admin/feeds', component: <AdminFeedsPage />},
    {path: '/a/admin/feed/debug', component: <AdminDebugFeedPage />},
    {path: '/a/admin/feed/import', component: <AdminFeedImportPage />},
    {path: '/a/admin/auto-feeds', component: <AdminAutoFeedsPage />},
    {path: '/a/admin/process/{id}', component: <AdminProcessFeedPage />},
    {path: '/a/admin/feed/{id}', component: <AdminViewFeedPage />},
    {path: '/a/admin/feed/{id}/repair', component: <AdminRepairFeedPage />},
    {path: '/a/admin/catalogs', component: <AdminCatalogsPage />},
    {path: '/a/admin/catalog/{id}', component: <AdminEditCatalogPage />},
    {path: '/a/admin/discover', component: <AdminDiscoverPage />},
    {path: '/a/admin/inactive-feeds', component: <AdminInactiveFeedsPage />},
    {path: '/a/admin/reports', component: <AdminReportsPage />},
    {path: '/a/admin/retention', component: <AdminRetentionPage />},
    {path: '/a/admin/images', component: <AdminImagesPage />},
    {path: '/a/admin/image/{id}', component: <AdminEditImagePage />},
    {path: '/a/admin/users', component: <AdminViewUserPage />},
    {path: '/a/admin/user/{id}', component: <AdminViewUserPage />},
    {path: '/a/admin/tags', component: <AdminManageTagsPage />},
    {path: '/a/admin/invitations', component: <AdminInvitationsPage />},
    {path: '/a/admin/invitation/{id}', component: <AdminViewInvitationPage />},
    {path: '/a/starred', component: <Main />},
    {path: '/a/read-later', component: <Main />},
    {path: '/a/feed/{id}', component: <Main />},
    {path: '/a/filter/{id}', component: <FeedFilterPage />},
    
    // Open a feed by its URL: resolve it and replace the location with /a/feed/{id}. /add also follows it.
    {path: '/add', component: <ViewFeedByUrlPage follow />},
    {path: '/view', component: <ViewFeedByUrlPage />},
    {path: '/login', component: <Login />},
    {path: '/signup', component: <Signup />},
    {path: '/forgot-password', component: <ForgotPassword />},
    {path: '/reset-password', component: <ResetPassword />},
    {path: '/enter-password', component: <OneTimePassword />},
    {path: '/confirm-email', component: <ConfirmEmail />},
    {path: '/accept-terms', component: <AcceptTerms />},
    {path: '/logout', component: <Logout />},
    {path: '/logged-out', component: <LoggedOut />},
  ];

  // Offline: every in-app page is replaced by the offline screen — nothing behind /a can load
  // or save while the server is unreachable. The redirects and the public/auth pages are left
  // alone, and OfflinePage still renders the sidebar, so browsing the feed list keeps working
  // (each pick lands back on the offline screen until the connection returns).
  // With offline reading the stored library and posts are used instead, so the pages stay.
  const activeRoutes = offlineMode == 'none' || canReadOffline ? routes
    : routes.map(r => r.component && (r.path == '/a' || r.path.startsWith('/a/')) ? {...r, component: <OfflinePage />} : r);

  return <GlobalKeyHandler>
      <ModalContainer>
        <Routes routes={activeRoutes} />
      </ModalContainer>
    </GlobalKeyHandler>;
};

export default AppRouter;
